import { EmitterService } from '../../services/emitter.service';
import { BaseClassHandler } from '../base-class-handler/base-class-handler';
import {
  BaseDraggableMarkerComponent,
  BaseDraggableMarkerParams,
} from '../base-draggable-marker/base-draggable-marker.component';
import {
  onAddingAnnotationDataChanged,
  onAnnotationClickClosedV2,
  onAnnotationClickOpenedV2,
  onAnnotationsStateChanged,
  onCancelAddingAnnotation,
  onDeleteCommentAnnotation,
} from '../../services/annotation.service';
import { Subject, takeUntil } from 'rxjs';
import { AiOptions, AnnotationParams } from '@mtm/conversation-client-sdk-annotation';

export class AudioAnnotationHandler implements BaseClassHandler {
  annotationPlugin: any;
  instanceId: string;
  item: any;
  player: any;
  marker: BaseDraggableMarkerComponent | null;
  itemId: string;
  private currentAnnotationRange: { start: number | null; end: number | null } | null = null;
  private ngUnsubscribe = new Subject<void>();
  private markerRangeListenersRegistered = false;
  constructor(player: any, annotationPlugin: any) {
    window['audioAnnotationHandler'] = this;
    this.annotationPlugin = annotationPlugin;
    this.item = this.annotationPlugin.options.item;
    this.instanceId = this.annotationPlugin.instanceId;
    this.player = player;
    this.itemId = this.item?.id;

    onDeleteCommentAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.deleteCommentAnnotation(args),
    });

    this.registerListeners();
  }

  get isAdding() {
    return this.annotationPlugin?.controls?.uiState?.adding && this.annotationPlugin?.controls?.uiState?.drawingEnabled;
  }

  setup() {}

  registerListeners() {
    this.annotationPlugin.registerListener('enteredAddingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) return;

      try {
        this.player?.pause?.();
      } catch (_) {}

      this.currentAnnotationRange = { start: null, end: null };
      this.unregisterAddingAnnotationRangeListeners();
      this.registerAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('exitingAddingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) return;
      if (this.marker) {
        this.marker.teardown();
        this.marker = null;
      }
      this.currentAnnotationRange = null;
      this.unregisterAddingAnnotationRangeListeners();
      onCancelAddingAnnotation.emit({ itemId: this.itemId, instanceId: this.instanceId });
    });

    // Enter edit mode for an existing annotation
    this.annotationPlugin.registerListener('enteredEditingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) return;
      this.pauseIfPlaying();

      const annotation = event?.detail?.annotation;
      const start = Number(annotation?.range?.start ?? 0);
      const rawEnd = Number(annotation?.range?.end ?? start);
      let end = rawEnd;
      if (end <= 0 || end <= start) {
        end = start + 0.05;
      } else if (start === end || end - start < 0.05) {
        end = start + 0.05;
      }
      const targetElement = document.getElementById('waveform-' + this.instanceId);
      const markerParams: BaseDraggableMarkerParams = {
        player: this.player,
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        targetElement,
        range: { start, end },
      };
      this.marker = new BaseDraggableMarkerComponent({ ...markerParams, forceRangeContainer: true });

      const dur =
        typeof this.player?.getDuration === 'function' ? this.player.getDuration() : (this.player?.duration?.() ?? 0);
      if (dur && dur > 0) {
        (this.marker as any).render?.();
        onAddingAnnotationDataChanged.emit({
          range: { start, end },
          itemId: this.itemId,
          instanceId: this.instanceId,
        });
      }

      if (annotation?.id) {
        onAnnotationClickOpenedV2.emit({
          annotation,
          itemId: this.itemId,
          instanceId: this.instanceId,
        });
      }
      this.unregisterAddingAnnotationRangeListeners();
      this.registerAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('exitingEditingAnnotation', (event: any) => {
      if (!this.isEventForThisInstance(event)) return;

      if (this.marker) {
        this.marker.teardown();
        this.marker = null;
      }
      onAnnotationClickClosedV2.emit({
        annotation: null,
        itemId: this.itemId,
        instanceId: this.instanceId,
      });
      this.unregisterAddingAnnotationRangeListeners();
    });

    this.annotationPlugin.registerListener('onStateChanged', (event: any) => {
      onAnnotationsStateChanged.emit({});
    });

    EmitterService.get('startRangeUpdate')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (event.instanceId !== this.instanceId) {
          return;
        }
        const seconds = event?.seconds ?? 0;
        if (typeof seconds === 'number' && seconds !== 0) {
          this.marker?.updateStart(seconds);
        }
      });

    EmitterService.get('endRangeUpdate')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event: any) => {
        if (event.instanceId !== this.instanceId) {
          return;
        }
        const seconds = event?.seconds ?? 0;
        if (typeof seconds === 'number' && seconds !== 0) {
          this.marker?.updateEnd(seconds);
        }
      });

    onAddingAnnotationDataChanged.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: any) => {
      if (this.isEventForThisInstance(event)) {
        if (event.range) {
          this.currentAnnotationRange = event.range;
        }
      }
    });

    this.player.on('audioprocess', () => {
      if (!this.marker?.dragging && this.marker?.range?.end === null) {
        this.marker?.timeUpdateChange();
      }
    });

    this.player.on('seek', () => {
      if (!this.marker?.dragging && this.marker?.range?.end === null) {
        this.marker?.timeUpdateChange();
      }
    });

    this.registerAddingAnnotationRangeListeners();
  }

  saveAnnotation(annotationComment: string, aiParams: AiOptions, isGeneralComment: boolean = false) {
    const fallbackRange = this.currentAnnotationRange ?? { start: null, end: null };
    const currentAnnotationRange = this.marker?.range ?? fallbackRange;
    const annotationParams: AnnotationParams = {
      shape: null,
      privateConditionKey: null,
      annotationComment: annotationComment,
      start: currentAnnotationRange.start,
      end: currentAnnotationRange.end,
      isGeneralComment: isGeneralComment,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
    };
    this.annotationPlugin.fire('saveNewAnnotation', { annotationParams: annotationParams });
  }

  saveEditedAnnotation(annotationComment: string, aiParams: AiOptions) {
    const annotationParams: AnnotationParams = {
      annotationComment: annotationComment,
      start: this.currentAnnotationRange.start,
      end: this.currentAnnotationRange.end,
      isGeneralComment: false,
      privateConditionKey: null,
      shape: null,
      agentVersionId: aiParams.agentVersionId ?? null,
      mediaQuality: aiParams.mediaQuality ?? null,
    };
    this.annotationPlugin.fire('saveEditedAnnotation', { annotationParams: annotationParams });
  }

  deleteCommentAnnotation(args: any) {
    if (args.instanceId != this.instanceId || args.itemId != this.item.id) {
      return;
    }
    this.annotationPlugin.fire('destroyComment', { id: args.comment.id, annotation: args.annotation });
  }

  teardown() {
    if (this.marker) {
      this.marker.teardown();
      this.marker = null;
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.player = null;
    this.annotationPlugin.fire('dispose');
  }

  private registerAddingAnnotationRangeListeners(): void {
    if (!this.annotationPlugin?.registerListener || this.markerRangeListenersRegistered) {
      return;
    }
    this.annotationPlugin.registerListener('addStartTimeRange', this.handleAddStartTimeRange);
    this.annotationPlugin.registerListener('removeStartTimeRange', this.handleRemoveStartTimeRange);
    this.annotationPlugin.registerListener('addEndTimeRange', this.handleAddEndTimeRange);
    this.annotationPlugin.registerListener('removeEndTimeRange', this.handleRemoveEndTimeRange);
    this.markerRangeListenersRegistered = true;
  }

  private unregisterAddingAnnotationRangeListeners(): void {
    if (!this.annotationPlugin?.unregisterListener) {
      return;
    }
    ['addStartTimeRange', 'removeStartTimeRange', 'addEndTimeRange', 'removeEndTimeRange'].forEach((type) =>
      this.annotationPlugin.unregisterListener(type)
    );
    this.markerRangeListenersRegistered = false;
  }

  private handleAddStartTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }
    this.pauseIfPlaying();
    const startTime = this.getCurrentTime();
    this.currentAnnotationRange = { start: startTime, end: null };

    this.ensureMarkerExists({ start: startTime, end: null });
    onAddingAnnotationDataChanged.emit({
      range: { start: startTime, end: null },
      itemId: this.itemId,
      instanceId: this.instanceId,
    });
  };

  private handleAddEndTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }

    this.pauseIfPlaying();
    const currentTime = this.getCurrentTime();
    const normalizedStart =
      typeof this.currentAnnotationRange?.start === 'number' ? this.currentAnnotationRange.start : currentTime;
    const normalizedEnd = normalizedStart + 0.05;

    if (!this.marker) {
      this.ensureMarkerExists({ start: normalizedStart, end: normalizedEnd });
    } else {
      this.marker.setStartTime(normalizedStart);
      this.marker.setEndTime(normalizedEnd);
    }

    this.currentAnnotationRange = { start: normalizedStart, end: normalizedEnd };
  };

  private handleRemoveStartTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }
    this.pauseIfPlaying();
    if (this.marker) {
      this.marker.teardown();
      this.marker = null;
    }
    this.currentAnnotationRange = null;
    onAddingAnnotationDataChanged.emit({
      range: { start: null, end: null },
      itemId: this.itemId,
      instanceId: this.instanceId,
    });
  };

  private handleRemoveEndTimeRange = (event: any): void => {
    if (!this.isEventForThisInstance(event)) {
      return;
    }
    this.pauseIfPlaying();
    if (this.marker) {
      this.marker.range.end = null;
      (this.marker as any).render?.();
    }
    const start = this.currentAnnotationRange?.start ?? null;
    this.currentAnnotationRange = start !== null ? { start, end: null } : null;
    onAddingAnnotationDataChanged.emit({
      range: { start, end: null },
      itemId: this.itemId,
      instanceId: this.instanceId,
    });
  };

  private ensureMarkerExists(range: { start: number | null; end: number | null }): void {
    if (this.marker) {
      return;
    }
    const targetElement = document.getElementById('waveform-' + this.instanceId);
    if (!targetElement) {
      return;
    }
    const startVal = range.start ?? this.getCurrentTime();
    const endVal = range.end ?? null;
    const markerParams: BaseDraggableMarkerParams = {
      player: this.player,
      annotationPlugin: this.annotationPlugin,
      instanceId: this.instanceId,
      targetElement,
      range: { start: startVal, end: endVal },
    };
    this.marker = new BaseDraggableMarkerComponent({ ...markerParams, forceRangeContainer: true });
  }

  private pauseIfPlaying(): void {
    try {
      if (this.player && typeof this.player.paused === 'function' && !this.player.paused()) {
        this.player.pause();
      }
    } catch (_) {}
  }

  private getCurrentTime(): number {
    if (typeof this.player?.getCurrentTime === 'function') {
      return this.player.getCurrentTime();
    }
    if (typeof this.player?.currentTime === 'function') {
      return this.player.currentTime();
    }
    return 0;
  }

  private isEventForThisInstance(event: any): boolean {
    if (event && event.instanceId) {
      return event.instanceId === this.instanceId;
    }
    if (event && event.itemId) {
      return event.itemId === this.itemId;
    }
    if (event && event.detail) {
      if (event.detail.instanceId) {
        return event.detail.instanceId === this.instanceId;
      }
      if (event.detail.itemId) {
        return event.detail.itemId === this.itemId;
      }
    }
    return true;
  }
}
